export interface AppError {
  message: string;
  serverMessage?: string;
  code?: string;
  statusCode?: number;
  details?: any;
  timestamp: string;
}

class ErrorService {
  /**
   * Extract a specific, human-readable message from a backend error payload.
   * Handles FastAPI's `detail`, which may be a string, a validation-error
   * array (422), or an object; falls back to `message` / `error`.
   */
  private extractServerMessage(data: any): string | undefined {
    if (!data || typeof data !== 'object') {
      return undefined;
    }

    const detail = data.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail)) {
      const joined = detail
        .map((e: any) =>
          typeof e === 'string'
            ? e
            : e?.msg
              ? `${e.loc ? e.loc.join('.') + ': ' : ''}${e.msg}`
              : JSON.stringify(e)
        )
        .join(', ');
      return joined || undefined;
    }
    if (detail && typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }

    if (typeof data.message === 'string') {
      return data.message;
    }
    if (typeof data.error === 'string') {
      return data.error;
    }
    return undefined;
  }

  /**
   * Convenience: parse any thrown value and return a user-friendly message.
   */
  toUserMessage(error: unknown): string {
    return this.getUserMessage(this.parseError(error));
  }

  /**
   * Parse different types of errors into a consistent format
   */
  parseError(error: any): AppError {
    const timestamp = new Date().toISOString();

    if (error == null) {
      return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR', timestamp };
    }

    // Handle RTK Query errors (FetchBaseQueryError). These are plain objects
    // (never Error instances) carrying a `status`: a numeric HTTP status with
    // `data`, or a string tag like 'FETCH_ERROR'/'TIMEOUT_ERROR' with `error`.
    if (typeof error === 'object' && !(error instanceof Error) && 'status' in error) {
      const rtk = error as { status: unknown; data?: any; error?: string };
      const serverMessage = this.extractServerMessage(rtk.data);
      const isNetworkError =
        rtk.status === 'FETCH_ERROR' || rtk.status === 'TIMEOUT_ERROR';

      return {
        message:
          serverMessage ||
          (typeof rtk.error === 'string' ? rtk.error : 'An unexpected error occurred'),
        serverMessage,
        code: rtk.data?.code || (isNetworkError ? 'NETWORK_ERROR' : undefined),
        statusCode: typeof rtk.status === 'number' ? rtk.status : undefined,
        details: rtk.data ?? error,
        timestamp,
      };
    }

    // Handle standard JavaScript errors
    if (error instanceof Error) {
      return {
        message: error.message,
        code: error.name,
        timestamp,
        details: { stack: error.stack },
      };
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        message: error,
        code: 'STRING_ERROR',
        timestamp,
      };
    }

    // Handle plain error-like objects (e.g. RTK Query SerializedError)
    if (error && typeof error === 'object' && typeof error.message === 'string') {
      return {
        message: error.message,
        code: typeof error.code === 'string' ? error.code : 'UNKNOWN_ERROR',
        timestamp,
        details: error,
      };
    }

    // Handle unknown errors
    return {
      message: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
      timestamp,
      details: error,
    };
  }

  /**
   * Get user-friendly error messages based on error codes or status
   */
  getUserMessage(error: AppError): string {
    // A specific message from the backend beats any generic fallback.
    if (error.serverMessage) {
      return error.serverMessage;
    }

    // Handle specific status codes
    if (error.statusCode) {
      switch (error.statusCode) {
        case 400:
          return 'Invalid request. Please check your input.';
        case 401:
          return 'Your session has expired. Please log in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 408:
          return 'Request timeout. Please try again.';
        case 429:
          return 'Too many requests. Please wait a moment before trying again.';
        case 500:
          return 'Server error. Please try again later.';
        case 502:
        case 503:
        case 504:
          return 'Service temporarily unavailable. Please try again later.';
      }

      // Any other status still reached the server — categorize it rather than
      // falling through to the network-error message below.
      if (error.statusCode >= 500) {
        return 'Server error. Please try again later.';
      }
      if (error.statusCode >= 400) {
        return 'Request failed. Please check your input and try again.';
      }
    }

    // Handle specific error codes
    switch (error.code) {
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'VALIDATION_ERROR':
        return error.message || 'Please check your input and try again.';
      case 'AUTH_ERROR':
        return 'Authentication failed. Please log in again.';
      case 'PERMISSION_ERROR':
        return 'You do not have permission to access this resource.';
      case 'TIMEOUT':
        return 'The operation timed out. Please try again.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }

  /**
   * Check if error is recoverable
   */
  isRecoverable(error: AppError): boolean {
    // Network errors are often recoverable
    if (error.code === 'NETWORK_ERROR' || error.statusCode === 408) {
      return true;
    }

    // Server errors might be temporary
    if (error.statusCode && error.statusCode >= 500) {
      return true;
    }

    // Rate limiting is temporary
    if (error.statusCode === 429) {
      return true;
    }

    return false;
  }
}

export default new ErrorService();
